import { t } from "../../i18n";
import { Link } from "react-router-dom";
import {
  Check,
  ChevronDown,
  FolderPlus,
  Ghost,
  MessageSquarePlus,
  Pencil,
  Search,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import type { Conversation, ConversationGroup, User } from "@lumen/shared";
import { ThemeSwitch } from "../ThemeSwitch";
import { FavoriteModelShortcuts } from "./FavoriteModelShortcuts";
import { SidebarFooter } from "../shared/SidebarFooter";
import type { DragEvent, ReactNode } from "react";

const UNGROUPED_DROP_TARGET = "__ungrouped__";

export function ConversationSidebar({
  user,
  setOpen,
  conversationSearch,
  setConversationSearch,
  groups,
  collapsedGroupIds,
  dragOverGroupId,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  conversationLink,
  ungroupedConversations,
  pinnedConversations,
  recentConversations,
  conversationsByGroup,
  renamingGroupId,
  setRenamingGroupId,
  groupRenameValue,
  setGroupRenameValue,
  setGroupToDelete,
  toggleGroupCollapsed,
  renameGroup,
  conversations,
  creatingGroup,
  setCreatingGroup,
  newGroupName,
  setNewGroupName,
  createGroup,
  groupError,
}: {
  user: User;
  setOpen: (value: boolean) => void;
  conversationSearch: string;
  setConversationSearch: (value: string) => void;
  groups: ConversationGroup[];
  collapsedGroupIds: Set<string>;
  dragOverGroupId: string | null;
  handleDragOver: (
    event: DragEvent<HTMLDivElement>,
    groupId: string | null,
  ) => void;
  handleDragLeave: (event: DragEvent<HTMLDivElement>) => void;
  handleDrop: (
    event: DragEvent<HTMLDivElement>,
    groupId: string | null,
  ) => void;
  conversationLink: (conversation: Conversation) => ReactNode;
  ungroupedConversations: Conversation[];
  pinnedConversations: Conversation[];
  recentConversations: Conversation[];
  conversationsByGroup: Map<string, Conversation[]>;
  renamingGroupId: string | null;
  setRenamingGroupId: (value: string | null) => void;
  groupRenameValue: string;
  setGroupRenameValue: (value: string) => void;
  setGroupToDelete: (value: ConversationGroup | null) => void;
  toggleGroupCollapsed: (id: string) => void;
  renameGroup: (group: ConversationGroup) => Promise<unknown>;
  conversations: Conversation[];
  creatingGroup: boolean;
  setCreatingGroup: (value: boolean) => void;
  newGroupName: string;
  setNewGroupName: (value: string) => void;
  createGroup: () => Promise<unknown>;
  groupError: string;
}) {
  return (
    <aside className="flex size-full min-h-0 flex-col border-r border-stone-200 bg-[#f4f7f5] p-3 sm:w-72 dark:border-white/10 dark:bg-[#121615]">
      <div className="flex h-12 items-center gap-2 px-2">
        <span className="grid size-8 place-items-center rounded-xl bg-lumen-600 text-white">
          <Sparkles size={17} />
        </span>
        <span className="text-lg font-bold tracking-tight">Lumen</span>
        <div className="ml-auto">
          <ThemeSwitch />
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="button p-2 lg:hidden"
          aria-label={t("Close sidebar")}
        >
          <X size={18} />
        </button>
      </div>
      <Link
        to="/"
        onClick={() => setOpen(false)}
        className="button-primary mt-4 justify-start"
      >
        <MessageSquarePlus size={17} /> {t("New conversation")}
      </Link>
      <Link to="/c/temporary" className="button mt-2 justify-start">
        <Ghost size={17} /> {t("Temporary chat")}
      </Link>
      <div className="relative mt-4">
        <Search
          size={15}
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-stone-400"
        />
        <input
          type="search"
          value={conversationSearch}
          onChange={(event) => setConversationSearch(event.target.value)}
          placeholder={t("Search conversations")}
          aria-label={t("Search conversation messages")}
          className="w-full rounded-xl border border-stone-200 bg-white py-2 pr-8 pl-9 text-sm transition outline-none focus:border-lumen-400 dark:border-white/10 dark:bg-white/5"
        />
        {conversationSearch && (
          <button
            type="button"
            onClick={() => setConversationSearch("")}
            className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"
            aria-label={t("Clear conversation search")}
          >
            <X size={13} />
          </button>
        )}
      </div>
      <nav
        data-testid="sidebar-conversations"
        className="mt-3 min-h-0 flex-1 space-y-4 overflow-y-auto"
      >
        <FavoriteModelShortcuts />
        {pinnedConversations.length > 0 && (
          <section>
            <div className="px-2 text-[11px] font-bold tracking-[.16em] text-stone-400 uppercase">
              {t("Pinned")}
            </div>
            <div className="mt-1 space-y-1">
              {pinnedConversations.map(conversationLink)}
            </div>
          </section>
        )}
        {conversationSearch ? (
          recentConversations.length > 0 && (
            <section>
              <div className="px-2 text-[11px] font-bold tracking-[.16em] text-stone-400 uppercase">
                {t("Matches")}
              </div>
              <div className="mt-1 space-y-1">
                {recentConversations.map(conversationLink)}
              </div>
            </section>
          )
        ) : (
          <>
            {groups.length > 0 && (
              <section>
                <div className="px-2 text-[11px] font-bold tracking-[.16em] text-stone-400 uppercase">
                  {t("Groups")}
                </div>
                <div className="mt-1 space-y-1">
                  {groups.map((group) => {
                    const inGroup = conversationsByGroup.get(group.id) ?? [];
                    const collapsed = collapsedGroupIds.has(group.id);
                    return (
                      <section key={group.id}>
                        {renamingGroupId === group.id ? (
                          <div className="flex items-center gap-1 px-2">
                            <input
                              autoFocus
                              type="text"
                              value={groupRenameValue}
                              onChange={(event) =>
                                setGroupRenameValue(event.target.value)
                              }
                              onKeyDown={(event) => {
                                if (event.key === "Enter")
                                  void renameGroup(group);
                                if (event.key === "Escape") {
                                  setRenamingGroupId(null);
                                  setGroupRenameValue("");
                                }
                              }}
                              placeholder={t("Group name")}
                              aria-label={t("Group name")}
                              className="field w-full min-w-0 px-2 py-1 text-sm"
                            />
                            <button
                              type="button"
                              className="button shrink-0 p-1.5"
                              onClick={() => void renameGroup(group)}
                              title={t("Rename")}
                              aria-label={t("Rename")}
                            >
                              <Check size={14} />
                            </button>
                            <button
                              type="button"
                              className="button shrink-0 p-1.5"
                              onClick={() => {
                                setRenamingGroupId(null);
                                setGroupRenameValue("");
                              }}
                              title={t("Cancel")}
                              aria-label={t("Cancel")}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <div
                            className={`group/header flex items-center gap-2 rounded-xl px-2 py-1 transition ${dragOverGroupId === group.id ? "ring-2 ring-lumen-400" : ""}`}
                            onDragOver={(event) =>
                              handleDragOver(event, group.id)
                            }
                            onDragLeave={handleDragLeave}
                            onDrop={(event) => handleDrop(event, group.id)}
                          >
                            <button
                              type="button"
                              onClick={() => toggleGroupCollapsed(group.id)}
                              aria-expanded={!collapsed}
                              title={
                                collapsed
                                  ? t("Expand group")
                                  : t("Collapse group")
                              }
                              className="flex min-w-0 flex-1 items-center gap-1.5 rounded-lg py-0.5 text-left text-sm font-semibold text-stone-500 transition hover:text-lumen-700 dark:text-stone-400 dark:hover:text-lumen-300"
                            >
                              <ChevronDown
                                size={13}
                                className={`shrink-0 text-stone-400 transition-transform duration-200 ${collapsed ? "-rotate-90" : ""}`}
                              />
                              <span className="min-w-0 flex-1 truncate">
                                {group.name}
                              </span>
                            </button>
                            <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition group-hover/header:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100">
                              <button
                                type="button"
                                className="rounded-lg p-1 text-stone-400 hover:bg-white hover:text-lumen-700 dark:hover:bg-white/10 dark:hover:text-lumen-300"
                                onClick={() => {
                                  setRenamingGroupId(group.id);
                                  setGroupRenameValue(group.name);
                                }}
                                title={t("Rename group")}
                                aria-label={t("Rename group")}
                              >
                                <Pencil size={13} />
                              </button>
                              <button
                                type="button"
                                className="rounded-lg p-1 text-stone-400 hover:bg-white hover:text-red-600 dark:hover:bg-white/10 dark:hover:text-red-400"
                                onClick={() => setGroupToDelete(group)}
                                title={t("Delete group")}
                                aria-label={t("Delete group")}
                              >
                                <Trash2 size={13} />
                              </button>
                            </span>
                          </div>
                        )}
                        {renamingGroupId === group.id && groupError && (
                          <p className="px-2 text-xs text-red-600 dark:text-red-300">
                            {groupError}
                          </p>
                        )}
                        {!collapsed && (
                          <div
                            className={`mt-1 space-y-1 rounded-xl p-1 transition ${dragOverGroupId === group.id ? "ring-2 ring-lumen-400" : ""}`}
                            onDragOver={(event) =>
                              handleDragOver(event, group.id)
                            }
                            onDragLeave={handleDragLeave}
                            onDrop={(event) => handleDrop(event, group.id)}
                          >
                            {inGroup.map(conversationLink)}
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              </section>
            )}
            {(groups.length > 0 || ungroupedConversations.length > 0) && (
              <section>
                <div className="px-2 text-[11px] font-bold tracking-[.16em] text-stone-400 uppercase">
                  {groups.length > 0 ? t("Ungrouped") : t("Recent")}
                </div>
                <div
                  className={`mt-1 space-y-1 rounded-xl p-1 transition ${dragOverGroupId === UNGROUPED_DROP_TARGET ? "ring-2 ring-lumen-400" : ""}`}
                  onDragOver={(event) =>
                    handleDragOver(event, UNGROUPED_DROP_TARGET)
                  }
                  onDragLeave={handleDragLeave}
                  onDrop={(event) => handleDrop(event, UNGROUPED_DROP_TARGET)}
                >
                  {ungroupedConversations.map(conversationLink)}
                </div>
              </section>
            )}
            <div className="px-2">
              {creatingGroup ? (
                <div className="flex items-center gap-1">
                  <input
                    autoFocus
                    type="text"
                    value={newGroupName}
                    onChange={(event) => setNewGroupName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") void createGroup();
                      if (event.key === "Escape") {
                        setCreatingGroup(false);
                        setNewGroupName("");
                      }
                    }}
                    placeholder={t("Group name")}
                    aria-label={t("Group name")}
                    className="field w-full min-w-0 px-2 py-1 text-sm"
                  />
                  <button
                    type="button"
                    className="button shrink-0 p-1.5"
                    disabled={!newGroupName.trim()}
                    onClick={() => void createGroup()}
                    title={t("Create group")}
                    aria-label={t("Create group")}
                  >
                    <Check size={14} />
                  </button>
                  <button
                    type="button"
                    className="button shrink-0 p-1.5"
                    onClick={() => {
                      setCreatingGroup(false);
                      setNewGroupName("");
                    }}
                    title={t("Cancel")}
                    aria-label={t("Cancel")}
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setCreatingGroup(true)}
                  className="flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left text-sm text-stone-500 transition hover:bg-white/70 hover:text-lumen-700 dark:text-stone-400 dark:hover:bg-white/5 dark:hover:text-lumen-300"
                >
                  <FolderPlus size={15} /> {t("New group")}
                </button>
              )}
              {creatingGroup && groupError && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-300">
                  {groupError}
                </p>
              )}
            </div>
          </>
        )}
        {conversations.length === 0 && (
          <p className="px-3 py-6 text-center text-xs leading-5 text-stone-400">
            {conversationSearch
              ? t("No conversations contain that text.")
              : t("Your conversations will appear here.")}
          </p>
        )}
      </nav>
      <SidebarFooter user={user} view="chat" setOpen={setOpen} />
    </aside>
  );
}
